// Lib Imports
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Box, Heading, Text } from 'grommet';
import EventEmitter from 'granite-admin/utils/event-emitter';
import RESET_MESSAGES from 'granite-admin/messages/forgotPassword';
import Steps from 'granite-admin/core/components/Steps';
import { useToast } from 'granite-admin/core/components/Toast';
import SplitLayout from 'granite-admin/core/components/SplitLayout';
import useQuery from 'granite-admin/utils/useQuery';
// Application Imports
import RequestTokenForm from './components/RequestTokenForm';
import ResetPasswordForm from './components/ResetPasswordForm';
import RequestSuccess from './components/RequestSuccess';
import { resetPasswordClicked, requestTokenClicked } from 'granite-admin/accounts/controllers/user';
import { RESET_EVENTS } from 'granite-admin/accounts/controllers/constants';
const primaryContact = process.env.REACT_APP_PRIMARY_CONTACT || 'EMAIL';
const allowAltRecovery = process.env.REACT_APP_ALLOW_ALTERNATE_RECOVERY || 'false';
const headings = ['Forgot Password?', '', 'Set New Password'];
const emailOrPhone = primaryContact === 'EMAIL' && allowAltRecovery === 'false' ? 'Email' : (primaryContact === 'EMAIL' || primaryContact === 'PHONE') && allowAltRecovery === 'true' ? 'Email or Phone' : primaryContact === 'PHONE' && allowAltRecovery === 'false' ? 'Phone' : '';
const subHeadings = [`Enter your registered ${emailOrPhone} to reset the password`];
const captions = ['We will send you reset password link.'];
function listenEvents({
  eventEmitter,
  successToast,
  errorToast,
  setActiveStep,
  history
}) {
  const observable = eventEmitter.getObservable();
  const subsciption = observable.subscribe(event => {
    switch (event.type) {
      case RESET_EVENTS.RESET_PASSWORD_SUCCESS:
        // *
        //  * NOTES:-
        //  *  - Page level logic are part of Page only.
        //  *  - LoginForm component only responsible for Login,
        //  *    not for what happend after Login - show toast or navigate
        //  *  - LoginForm is not responsible for in what layout
        //  *    that form component is being used.
        //  *  - Page dispatches Redux actions, navigates routes
        successToast(RESET_MESSAGES.RESET_PASSWORD_SUCCESS);
        history.push('/login');
        /* dispatch({
              type: 'accounts/user/UPDATE_PROFILE',
              data: event.data,
            }) */
        break;
      case RESET_EVENTS.RESET_PASSWORD_FAILURE:
        errorToast(RESET_MESSAGES.RESET_PASSWORD_FAILURE);
        break;
      case RESET_EVENTS.RESET_TOKEN_SUCCESS:
        successToast(RESET_MESSAGES.RESET_TOKEN_SUCCESS);
        break;
      case RESET_EVENTS.RESET_TOKEN_FAILURE:
        errorToast(RESET_MESSAGES.RESET_TOKEN_FAILURE);
        break;
      case RESET_EVENTS.RESET_SUCCESS:
        successToast(RESET_MESSAGES.RESET_SUCCESS);
        setActiveStep(1);
        break;
      case RESET_EVENTS.RESET_FAILURE:
        errorToast(event.data || RESET_MESSAGES.RESET_FAILURE);
        break;
      default:
        break;
    }
  });
  return subsciption;
}
const ResetPassword = ({
  history,
  sideContent,
  bannerPadding
}) => {
  const {
    query
  } = useQuery();
  const {
    successToast,
    errorToast
  } = useToast();
  const eventEmitter = useMemo(() => new EventEmitter(), []);
  const defaultActiveStep = query.token ? 2 : 0;
  const [organisation, setOrganisation] = useState([]);
  const [activeStep, setActiveStep] = useState(defaultActiveStep);
  const getSteps = () => {
    const reqTokenFormOnSubmit = async (values, {
      setSubmitting,
      setErrors
    }) => {
      let data = {
        email: values.email || null,
        phone: values?.phone?.number ? `+${values.phone.number}` : null,
        organisation_id: +values.organisation_id || null
      };
      setErrors({});
      setSubmitting(true);
      try {
        await requestTokenClicked(eventEmitter, data);
      } catch (e) {
        if (e.errors) {
          setOrganisation(e.errors.organisations);
          setErrors(e.errors);
        }
      }
      setSubmitting(false);
    };
    const resetPasswordFormOnSubmit = async (values, {
      setSubmitting,
      setErrors
    }) => {
      setErrors({});
      setSubmitting(true);
      try {
        await resetPasswordClicked(eventEmitter, {
          ...values,
          jwt_token: query.token
        });
      } catch (e) {
        if (e.errors) setErrors(e.errors);
      }
      setSubmitting(false);
    };
    return [{
      content: /*#__PURE__*/React.createElement(RequestTokenForm, {
        onSubmit: reqTokenFormOnSubmit,
        query: query,
        organisation: organisation,
        setOrganisation: setOrganisation
      }),
      icon: /*#__PURE__*/React.createElement(React.Fragment, null)
    }, {
      content: /*#__PURE__*/React.createElement(RequestSuccess, null),
      icon: /*#__PURE__*/React.createElement(React.Fragment, null)
    }, {
      content: /*#__PURE__*/React.createElement(ResetPasswordForm, {
        onSubmit: resetPasswordFormOnSubmit
      }),
      icon: /*#__PURE__*/React.createElement(React.Fragment, null)
    }];
  };
  useEffect(function init() {
    const subscription = listenEvents({
      eventEmitter,
      successToast,
      errorToast,
      // setToastData,
      setActiveStep,
      history
    });
    return () => subscription.unsubscribe();
  }, [eventEmitter, history, successToast, errorToast]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SplitLayout, {
    mainContent: /*#__PURE__*/React.createElement(Box, {
      width: "large"
    }, /*#__PURE__*/React.createElement(Heading, {
      level: 3
    }, headings[activeStep]), subHeadings[activeStep] && /*#__PURE__*/React.createElement(Heading, {
      color: "dark-2",
      level: 5,
      size: "large"
    }, subHeadings[activeStep]), /*#__PURE__*/React.createElement(Text, {
      size: "medium"
    }, captions[activeStep]), /*#__PURE__*/React.createElement(Steps, {
      margin: "xsmall",
      steps: getSteps(),
      activeStep: activeStep,
      onChange: () => false,
      navigationVisible: false
    })),
    sideContent: sideContent,
    bannerPadding: bannerPadding,
    mainProps: {
      style: {
        marginTop: '3%'
      }
    }
  }));
};
ResetPassword.defaultProps = {
  bannerPadding: 'large',
  sideContent: null
};
ResetPassword.propTypes = {
  bannerPadding: PropTypes.string,
  sideContent: PropTypes.node,
  history: PropTypes.object
};
export default withRouter(ResetPassword);